import { Box, PageContent, Text } from 'grommet';
import { CircleInformation } from 'grommet-icons';
import React from 'react';
import { Navigate, useParams } from 'react-router';

import { pgaPoolApi } from '../api/pga-pool';
import { Spinner } from '../components/Spinner';
import { resolveCurrentWeekTournament } from '../components/TournamentLeaderboard/resolveTournament';

import { withPageLayout } from './withPageLayout';

import { PgaTournamentTournamentStatusEnum } from '@drewkimberly/pga-pool-api';

function _PoolLeaderboardAliasPage() {
  const params = useParams();
  const poolId = params.poolId;
  const [redirectPath, setRedirectPath] = React.useState<string | null>(null);
  const [isLoading, setIsLoading] = React.useState(true);
  const [error, setError] = React.useState<Error | undefined>(undefined);

  React.useEffect(() => {
    let isMounted = true;

    async function resolveTournamentRedirect() {
      if (!poolId) {
        setError(new Error('A pool ID is required for this page.'));
        setIsLoading(false);
        return;
      }

      setIsLoading(true);
      setError(undefined);

      try {
        const tournament = await resolveCurrentWeekTournament(poolId);
        if (!isMounted) {
          return;
        }

        if (!tournament) {
          setRedirectPath(`/pools/${poolId}/tournaments`);
          return;
        }

        const status = tournament.pga_tournament.tournament_status;
        const subTab =
          status === PgaTournamentTournamentStatusEnum.InProgress
            ? 'leaderboard'
            : status === PgaTournamentTournamentStatusEnum.Completed
              ? 'results'
              : null;

        const basePath = `/pools/${poolId}/tournaments/${tournament.id}`;
        setRedirectPath(subTab ? `${basePath}/${subTab}` : basePath);
      } catch (e) {
        if (!isMounted) {
          return;
        }

        if (!pgaPoolApi.is404Error(e as Error)) {
          console.error(e);
          setError(e as Error);
        } else {
          setError(new Error('Pool not found.'));
        }
      } finally {
        if (isMounted) {
          setIsLoading(false);
        }
      }
    }

    resolveTournamentRedirect();

    return () => {
      isMounted = false;
    };
  }, [poolId]);

  if (redirectPath) {
    return <Navigate to={redirectPath} replace />;
  }

  if (isLoading) {
    return (
      <PageContent>
        <Spinner />
      </PageContent>
    );
  }

  if (error) {
    return (
      <PageContent>
        <Box height="medium" round="small" align="center" justify="center">
          <CircleInformation size="large" />
          <Text size="large" textAlign="center" margin="small">
            {error.message}
          </Text>
        </Box>
      </PageContent>
    );
  }

  return (
    <PageContent>
      <Box height="medium" round="small" align="center" justify="center">
        <CircleInformation size="large" />
        <Text size="large" textAlign="center" margin="small">
          No PGA Tournament event is currently active! Please check back later
        </Text>
      </Box>
    </PageContent>
  );
}

export const PoolLeaderboardAliasPage = withPageLayout(_PoolLeaderboardAliasPage);
